import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import { useUrlSearchParams } from '@dai/common'
import {
  Download as DownloadIcon,
  Edit as EditIcon,
  MoreVert,
  UploadFile,
} from '@mui/icons-material'
import {
  Alert,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import { SpacingProps } from '@mui/system'
import { pdf } from '@react-pdf/renderer'
import { saveAs } from 'file-saver'
import { isNil, snakeCase, uniq } from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { useRefresh } from '@/contexts/RefreshContext'
import {
  DisplayOrder,
  GET_MASTER_CATEGORIES,
  GET_PRODUCTS,
  GET_TENANT_DISPLAYS,
  GetMasterCategoriesQuery,
  GetMasterCategoriesQueryVariables,
  GetProductsQuery,
  GetProductsQueryVariables,
  GetTenantDisplaysQuery,
  GetTenantDisplaysQueryVariables,
  Ordering,
  ProductFragment,
  SortDirection,
  UPDATE_DISPLAYS_ORDER,
} from '@/graphql'
import useLoggedInUser from '@/hooks/useLoggedInUser'
import { useTableOrder } from '@/hooks/useTableOrder'
import { useTablePageState } from '@/hooks/useTablePageState'
import useT from '@/i18n/useT'
import DeliciousTable, { HeadCell } from '@/pages/components/DeliciousTable'
import { ImportManyPlanogramsDialog } from '@/pages/planogram/components/ImportManyPlanogramsDialog'
import PlanogramPDF from '@/pages/planogram/components/PlanogramPDF'
import { LocalizationHelpers } from '@/utils/localization.helpers'

import { DeleteDisplayDialog } from './DeleteDisplayDialog'
import type { DisplayRow } from './display-table.types'
import { DisplayTableReorder } from './DisplayTableReorder'
import { EditDisplayDialog } from './EditDisplayDialog'

export interface ChipProps extends SpacingProps {
  status: 'active' | 'inactive'
}

/** Reorder loads all store displays in one query; paginating reorder would break global order. */
const DISPLAY_REORDER_QUERY_LIMIT = 500

type Props = {
  storeId: string
}

export const DisplayTable: React.FC<Props> = ({ storeId }) => {
  const { t } = useT()
  const {
    handle: { handleNavTo },
  } = useUrlSearchParams()

  const { id: singleStoreId } = useParams()

  const { user } = useLoggedInUser()

  const { order, orderBy, handleRequestSort } = useTableOrder<DisplayOrder>(
    'name',
    SortDirection.DESC,
  )

  const { page, rowsPerPage, setPage, setRowsPerPage } = useTablePageState({
    initialRowsPerPage: 25,
  })

  const [getDisplays, { data: displaysData, loading, error }] = useLazyQuery<
    GetTenantDisplaysQuery,
    GetTenantDisplaysQueryVariables
  >(GET_TENANT_DISPLAYS, { notifyOnNetworkStatusChange: true })

  const fetchTableDisplays = useCallback(() => {
    return getDisplays({
      variables: {
        filters: { store: { id: storeId } },
        pagination: { limit: rowsPerPage, offset: rowsPerPage * page },
      },
      fetchPolicy: 'network-only',
    })
  }, [getDisplays, storeId, rowsPerPage, page])

  useRefresh(() => {
    void fetchTableDisplays()
  })

  useEffect(() => {
    void fetchTableDisplays()
  }, [fetchTableDisplays])

  const headCells: HeadCell<DisplayOrder>[] = [
    {
      id: 'image',
      alignment: 'left',
      label: t('displaysTable.imageHeader'),
    },
    {
      id: 'name',
      alignment: 'left',
      label: t('displaysTable.nameHeader'),
    },
    {
      id: 'order',
      alignment: 'left',
      label: t('displaysTable.orderHeader'),
    },
    {
      id: 'masterCategory',
      alignment: 'left',
      label: t('displaysTable.masterCategoryHeader'),
    },
    {
      id: 'actions',
      alignment: 'right',
      label: '',
    },
  ]

  const [rowMenuAnchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  )
  const [rowMenuDisplay, setRowMenuDisplay] = useState<DisplayRow | null>(null)
  const [displayToEdit, setDisplayToEdit] = useState<DisplayRow | null>(null)
  const [displayToDelete, setDisplayToDelete] = useState<DisplayRow | null>(
    null,
  )
  const [reorderMode, setReorderMode] = useState(false)
  const [reorderRows, setReorderRows] = useState<DisplayRow[]>([])
  const [reorderLoading, setReorderLoading] = useState(false)
  const [reorderError, setReorderError] = useState<string | null>(null)

  const [updateDisplaysOrder, { loading: savingOrder }] = useMutation(
    UPDATE_DISPLAYS_ORDER,
  )

  const handleRowMenuClose = () => {
    setRowMenuAnchorEl(null)
    setRowMenuDisplay(null)
  }

  const renderRow = (
    display: DisplayRow,
    index: number,
    onRowClick: (displayId: string) => void,
  ) => {
    return (
      <TableRow
        hover
        role="checkbox"
        tabIndex={-1}
        key={`${display.id}`}
        onClick={() => onRowClick(display.id)}
        sx={{ cursor: 'pointer' }}
      >
        <TableCell align="left">
          {display.imageUrl ? (
            <img
              src={display.imageUrl}
              alt={display.name}
              style={{ height: 175, objectFit: 'contain' }}
            />
          ) : null}
        </TableCell>
        <TableCell align="left">
          <Typography>{display.name}</Typography>
        </TableCell>
        <TableCell align="left">
          <Typography>{display.order}</Typography>
        </TableCell>
        <TableCell align="left">
          <Typography>{display.masterCategory}</Typography>
        </TableCell>
        <TableCell align="right" onClick={e => e.stopPropagation()}>
          <IconButton
            aria-label={t('displaysTable.rowActions', 'Display actions')}
            onClick={e => {
              e.stopPropagation()
              setRowMenuAnchorEl(e.currentTarget)
              setRowMenuDisplay(display)
            }}
          >
            <MoreVert />
          </IconButton>
        </TableCell>
      </TableRow>
    )
  }

  const { data: categoryData } = useQuery<
    GetMasterCategoriesQuery,
    GetMasterCategoriesQueryVariables
  >(GET_MASTER_CATEGORIES, { fetchPolicy: 'cache-and-network' })

  const mapToDisplayRow = (
    display: GetTenantDisplaysQuery['tenantDisplays']['results'][number],
  ): DisplayRow => ({
    name: display.name,
    imageUrl: display.image?.url,
    id: display.id,
    sortOrder: display.order,
    order: LocalizationHelpers.ordinalize(t, display.order + 1),
    displayType: display.displayType?.name || t('common.notApplicable'),
    masterCategory:
      categoryData?.masterCategories.results.find(
        category => category.id === display.masterCategoryId,
      )?.name || t('common.notApplicable'),
    masterCategoryId: display.masterCategoryId || '',
  })

  const data: DisplayRow[] =
    displaysData?.tenantDisplays.results.map(mapToDisplayRow) || []

  const enterReorderMode = async () => {
    setReorderLoading(true)
    setReorderError(null)
    try {
      const { data: reorderData } = await getDisplays({
        variables: {
          filters: { store: { id: storeId } },
          order: { order: Ordering.ASC },
          pagination: { limit: DISPLAY_REORDER_QUERY_LIMIT, offset: 0 },
        },
        fetchPolicy: 'network-only',
      })
      setReorderRows(
        reorderData?.tenantDisplays.results.map(mapToDisplayRow) ?? [],
      )
      setReorderMode(true)
    } catch (err) {
      setReorderError(
        err instanceof Error
          ? err.message
          : t('displaysTable.reorderLoadError', 'Failed to load displays'),
      )
    } finally {
      setReorderLoading(false)
    }
  }

  const handleSaveReorder = async () => {
    setReorderError(null)
    try {
      await updateDisplaysOrder({
        variables: {
          filters: { store: { id: storeId } },
          data: reorderRows.map((row, index) => ({
            id: row.id,
            order: index,
          })),
        },
      })
      setReorderMode(false)
      setReorderRows([])
      await fetchTableDisplays()
    } catch (err) {
      setReorderError(
        err instanceof Error
          ? err.message
          : t('displaysTable.reorderSaveError', 'Failed to save display order'),
      )
    }
  }

  const handleCancelReorder = () => {
    setReorderMode(false)
    setReorderError(null)
    setReorderRows([])
    void fetchTableDisplays()
  }

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const [showImportManyPlanogramsDialog, setShowImportManyPlanogramsDialog] =
    useState(false)
  const handleImportClick = () => {
    setShowImportManyPlanogramsDialog(true)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const [getProducts] = useLazyQuery<
    GetProductsQuery,
    GetProductsQueryVariables
  >(GET_PRODUCTS)

  const [loadingPDF, setLoadingPDF] = useState(false)

  const handleGeneratePDF = async (reversed = false) => {
    setLoadingPDF(true)
    try {
      if (!displaysData) return

      const displaysForPDF = []
      for (let i = 0; i < displaysData.tenantDisplays.results.length; i++) {
        const d = displaysData.tenantDisplays.results[i]
        if (!d.planogram) {
          continue
        }
        const upcs: string[] = uniq([
          ...(d.planogram?.shapedUpcs.flatMap(segment =>
            segment.flatMap(shelf => shelf.flatMap(facing => facing)),
          ) || []),
        ]).sort()

        const results = await getProducts({ variables: { filters: { upcs } } })

        const planogramProducts: ProductFragment[][] =
          d.planogram?.shapedUpcs[0].map(shelf =>
            shelf
              .map(facing => {
                const product = results.data?.products?.results.find(
                  product => product.upcA === facing,
                )
                return product
              })
              .filter(product => !isNil(product)),
          ) as ProductFragment[][]

        displaysForPDF.push({
          name: d.name,
          storeName: d.store.location?.name,
          reversed,
          products: planogramProducts,
        })
      }

      const blob = await pdf(
        <PlanogramPDF t={t} displays={displaysForPDF} />,
      ).toBlob()
      const filePrefix = singleStoreId
        ? snakeCase(
            displaysData.tenantDisplays.results[0].store.location?.name ||
              'store',
          )
        : user?.tenant?.name || 'tenant'
      saveAs(
        blob,
        reversed
          ? `${filePrefix}_planograms_back.pdf`
          : `${filePrefix}_planograms_front.pdf`,
      )
    } finally {
      setLoadingPDF(false)
    }
  }

  return (
    <>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          {reorderMode ? (
            <>
              <Button
                onClick={handleCancelReorder}
                sx={{ height: 50 }}
                disabled={savingOrder}
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSaveReorder}
                variant="contained"
                sx={{ height: 50 }}
                loading={savingOrder}
              >
                {t('common.done', 'Done')}
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={enterReorderMode}
                variant="contained"
                sx={{ height: 50 }}
                startIcon={<EditIcon />}
                loading={reorderLoading}
                loadingPosition="start"
              >
                {t('displaysTable.editOrder', 'Edit order')}
              </Button>
              <Button
                onClick={handleImportClick}
                variant="contained"
                aria-owns={anchorEl ? 'simple-menu' : undefined}
                aria-haspopup="true"
                sx={{ height: 50 }}
                startIcon={<UploadFile />}
                loadingPosition="start"
              >
                {t('planogramList.import')}
              </Button>
              <Button
                onClick={handleClick}
                variant="contained"
                aria-owns={anchorEl ? 'simple-menu' : undefined}
                aria-haspopup="true"
                sx={{ height: 50 }}
                startIcon={<DownloadIcon />}
                loading={loadingPDF}
                loadingPosition="start"
              >
                {t('planogramList.print')}
              </Button>
              <Menu
                id={'simple-menu'}
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem
                  onClick={() => {
                    handleClose()
                    handleGeneratePDF(false)
                  }}
                >
                  {t('planogramList.print.regular')}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleClose()
                    handleGeneratePDF(true)
                  }}
                >
                  {t('planogramList.print.reverse')}
                </MenuItem>
              </Menu>
            </>
          )}
        </Stack>
        {reorderError && !reorderMode && (
          <Alert severity="error" onClose={() => setReorderError(null)}>
            {reorderError}
          </Alert>
        )}
        {reorderMode ? (
          <DisplayTableReorder
            rows={reorderRows}
            onReorder={setReorderRows}
            loading={reorderLoading}
            error={reorderError}
          />
        ) : (
          <DeliciousTable<DisplayRow, DisplayOrder>
            data={data}
            headCells={headCells}
            renderRow={(display, index) =>
              renderRow(display, index, displayId =>
                handleNavTo(`stores/${storeId}/planograms/${displayId}`),
              )
            }
            order={order === 'ASC' ? 'asc' : 'desc'}
            orderBy={orderBy}
            handleRequestSort={handleRequestSort}
            loading={loading}
            error={error?.message}
            totalCount={displaysData?.tenantDisplays.totalCount || 0}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setRowsPerPage}
            title={t('displaysTable.title')}
          />
        )}
      </Stack>
      <ImportManyPlanogramsDialog
        open={showImportManyPlanogramsDialog}
        onClose={() => setShowImportManyPlanogramsDialog(false)}
        onSuccess={() => {
          setShowImportManyPlanogramsDialog(false)
          void fetchTableDisplays()
        }}
        storeId={storeId}
      />
      <Menu
        anchorEl={rowMenuAnchorEl}
        open={Boolean(rowMenuAnchorEl)}
        onClose={handleRowMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (rowMenuDisplay) {
              setDisplayToEdit(rowMenuDisplay)
            }
            handleRowMenuClose()
          }}
        >
          {t('common.edit')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (rowMenuDisplay) {
              setDisplayToDelete(rowMenuDisplay)
            }
            handleRowMenuClose()
          }}
        >
          {t('common.delete')}
        </MenuItem>
      </Menu>
      {displayToEdit && (
        <EditDisplayDialog
          open
          onClose={() => setDisplayToEdit(null)}
          storeId={storeId}
          displayId={displayToEdit.id}
          initialName={displayToEdit.name}
          initialMasterCategoryId={displayToEdit.masterCategoryId}
          onSaved={() => {
            setDisplayToEdit(null)
            void fetchTableDisplays()
          }}
        />
      )}
      {displayToDelete && (
        <DeleteDisplayDialog
          open
          onClose={() => setDisplayToDelete(null)}
          storeId={storeId}
          displayId={displayToDelete.id}
          displayName={displayToDelete.name}
          refetchQueryVariables={{
            filters: { store: { id: storeId } },
            pagination: { limit: rowsPerPage, offset: rowsPerPage * page },
          }}
          onSuccess={() => {
            setDisplayToDelete(null)
            void fetchTableDisplays()
          }}
        />
      )}
    </>
  )
}
